import React, { useState, useEffect } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { Locales } from '@/types/i18n';
import { setLocaleCookie } from '@/lib/i18n/setLocalCookie';
import { getNewPathname } from '@/lib/i18n/getNewPathName';
import i18nConfig from '@i18nConfig';
import styles from './MobileDrawer.module.css';
import StampLogo from '../logo/StampLogo';
import CloseX from '../icons/CloseX';
// Import translations
import enTranslation from '@/locales/en/translation.json';
import esTranslation from '@/locales/es/translation.json';
import deTranslation from '@/locales/de/translation.json';
import itTranslation from '@/locales/it/translation.json';
import zhCNTranslation from '@/locales/zh_CN/translation.json';

// Define a type for nested translation objects
type NestedTranslation = {
  [key: string]: string | NestedTranslation;
};

// Helper function to get translation based on locale
const getTranslation = (locale: Locales, key: string): string => {
  const keys = key.split('.');
  let translationObj: NestedTranslation;

  switch (locale) {
    case 'en':
      translationObj = enTranslation;
      break;
    case 'es':
      translationObj = esTranslation;
      break;
    case 'de':
      translationObj = deTranslation;
      break;
    case 'it':
      translationObj = itTranslation;
      break;
    case 'zh-CN':
      translationObj = zhCNTranslation;
      break;
    default:
      translationObj = enTranslation;
  }

  // Navigate through the nested keys
  let result: string | NestedTranslation = translationObj;
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      return key; // Return the key if translation not found
    }
  }

  return typeof result === 'string' ? result : key;
};

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const MerchantsMobileDrawer: React.FC<MobileDrawerProps> = ({ open, onClose }) => {
  const router = useRouter();
  const params = useParams();
  const locale = (params.locale as Locales) || 'en';
  const [selectedMarket, setSelectedMarket] = useState<'es' | 'it'>('es'); // Default to Spanish market

  // Check URL for market parameter and country cookie on component mount
  useEffect(() => {
    const url = new URL(window.location.href);
    const marketParam = url.searchParams.get('market');
    
    if (marketParam === 'it') {
      // URL parameter takes precedence
      setSelectedMarket('it');
    } else if (marketParam === 'es') {
      setSelectedMarket('es');
    } else {
      // If no URL parameter, check the country cookie
      const cookies = document.cookie.split(';');
      const countryCookie = cookies.find(cookie => cookie.trim().startsWith('user-country='));
      const country = countryCookie ? countryCookie.split('=')[1].trim() : '';
      
      // Set market based on detected country
      if (country === 'IT') {
        setSelectedMarket('it');
      } else {
        // Default to Spanish market for all other countries (including ES)
        setSelectedMarket('es');
      }
    }
  }, []);

  // Handle market change
  const handleMarketChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMarket = event.target.value === 'italian' ? 'it' : 'es';
    setSelectedMarket(newMarket);

    // Update URL query parameter
    const url = new URL(window.location.href);
    if (newMarket === 'it') {
      url.searchParams.set('market', 'it');
    } else {
      url.searchParams.delete('market');
    }
    window.history.pushState({}, '', url);
  };
  if (!open) return null;
  return (
    <div className={styles.overlay}>
      <aside className={styles.drawer}>
        <div className={styles.headerRow}>
          <StampLogo className={styles.logo} />
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close menu">
            <CloseX />
          </button>
        </div>
        <div className={styles.menuContent}>
          {/* Market selection */}
          <div className={styles.selectRow}>
            <select
              className={styles.select}
              value={selectedMarket === 'es' ? 'spanish' : 'italian'}
              onChange={handleMarketChange}
            >
              <option value="spanish">{getTranslation(locale, 'navbar.forSpanishMarket')}</option>
              <option value="italian">{getTranslation(locale, 'navbar.forItalianMarket')}</option>
            </select>
          </div>

          {/* Navigation links */}
          <nav className={styles.navLinks}>
            <a
              href="https://tally.so/r/wzOPWE"
              className={`${styles.navLink} ${styles.contactSalesLink}`}
              target="_blank"
            >
              {getTranslation(locale, 'navbar.contactSales')}
            </a>
            <a
              href={
                selectedMarket === 'it'
                  ? 'https://signup.stampit.co/'
                  : 'https://sales.askforstamp.com/signup'
              }
              className={`${styles.navLink} ${styles.signUpLink}`}
              target="_blank"
            >
              {getTranslation(locale, 'navbar.signUp')}
            </a>

            <hr className={styles.divider} />
            <a
              href={
                selectedMarket === 'it'
                  ? 'https://signup.stampit.co/'
                  : 'https://sales.askforstamp.com/signup'
              }
              className={`${styles.navLink} ${styles.loginLink}`}
              target="_blank"
            >
              {getTranslation(locale, 'navbar.login')}
            </a>
            <hr className={styles.divider} />
          </nav>

          {/* Language selection */}
          <div className={`${styles.selectRow} ${styles.languageSelect}`}>
            <select
              className={styles.select}
              value={locale}
              onChange={(e) => {
                const newLocale = e.target.value as Locales;
                setLocaleCookie(newLocale);
                let newPath = getNewPathname(newLocale, locale);

                // Preserve market parameter if it exists
                if (typeof window !== 'undefined') {
                  const params = new URLSearchParams(window.location.search);
                  const market = params.get('market');
                  if (market) {
                    newPath += (newPath.includes('?') ? '&' : '?') + `market=${market}`;
                  }
                }

                router.push(newPath);
                onClose(); // Close the drawer after changing language
              }}
            >
              {i18nConfig.languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </aside>
      <div className={styles.backdrop} onClick={onClose} />
    </div>
  );
};
